import {BaseResponseModel} from '../../models/base-response.model';
import {Utils} from '../../common/utils';
import {environment} from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ParamsInterface } from '../OffService/params.interface';
import { inject } from '@angular/core';

export enum HttpRequestType {
    GET, POST, PUT, DELETE
}

export class BaseApi<T> {
    public inject: {
        params: {
            id_cliente?: (endpoint: string, params: ParamsInterface) => string | null,
            id_usuario?: (endpoint: string, params: ParamsInterface) => string | null,
            tipo_usuario?: (endpoint: string, params: ParamsInterface) => string | null
        }
        payload: {
            id_cliente?: (endpoint: string, data: ParamsInterface) => string | null,
            id_usuario?: (endpoint: string, data: ParamsInterface) => string | null,
        },
    } = {
            params: {},
            payload: {}
        };
    
    private readonly http = inject(HttpClient);
    private BASE_URL: string = !environment.serverUrl.includes('localhost') ? environment.serverUrl + 'ws/' : 'http://localhost:8000/';
    private BASE_URL_SIEX = 'https://locatec.es/proyectos/SIEX/ws/';
    private BASE_URL_FITOSGEST = 'https://locatec.es/proyectos/fitogest/ws/';
    private BASE_URL_AUX: string = !environment.serverUrl.includes('localhost') ? environment.serverUrl + 'ws/' : 'http://localhost:8000/';
    private __: HttpClient;

    constructor(http: HttpClient) {
        this.__ = http;
    }


    public proxyCheck: ((type: HttpRequestType, endpoint: string, data: ParamsInterface | undefined, ) => boolean) = () => true;


    
    protected get(endpoint: string, params?: ParamsInterface): Promise<BaseResponseModel<T>> {
        switch (endpoint) {
        case 'cultivo_variedad/get_cultivos':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'cultivo_variedad/get_variedades':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'generic/vista_cultivo_variedad':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'generic/_buenas_practicas_fertilizantes':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'generic/_buenas_practicas_riego':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'generic/_metodos_aplicacion_fertilizante':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'generic/_unidades_medida':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'generic/_tipos_fertilizacion':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'parcelas/get_portainjertos':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'parcelas/get_sistemas_riego':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'tareas/justificaciones_get':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'tareas/actividades_sobre_cubierta_get':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'generic/vademecum_productos':
            this.BASE_URL = this.BASE_URL_FITOSGEST;
            break;
        case 'generic/_especies_animales':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        case 'generic/_comunidades_autonomas':
            this.BASE_URL = this.BASE_URL_SIEX;
            break;
        default:
            this.BASE_URL = this.BASE_URL_AUX;
            break;
        }
        const url =  
        !environment.serverUrl.includes('localhost') ? 
            this.BASE_URL + this.injectParams(this.changeURL(endpoint), params) : 
            this.BASE_URL + endpoint;

        return this.proxy(HttpRequestType.GET, endpoint, params, () => this.__
            .get(url, {
                params: ((p) => {
                    const filteredParams: Record<string,string> = {};

                    for (const i in p) {
                        if (p[i] !== undefined && p[i] !== null) {
                            filteredParams[i] = p[i] ?? '';
                        }
                    }

                    return filteredParams;
                })(params)
            })
            .toPromise()
            .then<BaseResponseModel<T>, never>(
                this.handleResponse,
                this.handleError));
    }

    protected put(endpoint: string, data: ParamsInterface | undefined): Promise<BaseResponseModel<T>> {
        this.BASE_URL = this.BASE_URL_AUX;
        return this.proxy(HttpRequestType.PUT, endpoint, data, () => this.__
            .put(
                this.BASE_URL + this.injectParams(this.changeURL(endpoint), data), JSON.stringify(this.injectPayload(data ?? {}, endpoint))
            )
            .toPromise()
            .then<BaseResponseModel<T>, never>(
                this.handleResponse,
                this.handleError));
    }

    protected delete(endpoint: string, data: ParamsInterface | undefined): Promise<BaseResponseModel<T>> {
        this.BASE_URL = this.BASE_URL_AUX;
        return this.proxy(HttpRequestType.DELETE, endpoint, data, () => this.__
            .delete(this.BASE_URL + this.injectParams(this.changeURL(endpoint), data), data as Record<string,string>)
            .toPromise()
            .then<BaseResponseModel<T>, never>(
                this.handleResponse,
                this.handleError));
    }

    protected async post(endpoint: string, data?: ParamsInterface): Promise<BaseResponseModel<T>> {
        this.BASE_URL = this.BASE_URL_AUX;

        const url = !environment.serverUrl.includes('localhost')
            ? this.BASE_URL + this.injectParams(this.changeURL(endpoint), data)
            : this.BASE_URL + endpoint;

        if (environment.serverUrl.includes('localhost') || environment.serverUrl.includes('127.0.0.1')) {
            await this.ensureCsrfOnce();
        }
        const payload = this.injectPayload(data ?? {}, endpoint);

        return this.proxy(HttpRequestType.POST, endpoint, data, () =>
            this.__
            .post(url, payload, { withCredentials: environment.serverUrl.includes('localhost') })
            .toPromise()
            .then<BaseResponseModel<T>, never>(this.handleResponse, this.handleError)
        );
    }

    protected handleResponse(response: object | undefined): BaseResponseModel<T> {
        if (Utils.isSet(response)) {
            return response as BaseResponseModel<T>;
        } else {
            return ({}) as BaseResponseModel<T>;
        }
    }

    protected handleError(error: unknown): PromiseLike<never> {
        throw error;
    }

    protected getEntityData(baseResponse: BaseResponseModel<T>): T {
        return baseResponse ?
            baseResponse.data ?
                baseResponse.data.length > 0 ?
                    baseResponse.data[0] as T
                    : null as T
                : null as T
            : null as T;
    }

    protected getEntityDataList(baseResponse: BaseResponseModel<T>): T[] {
        return baseResponse ?
            baseResponse.data ?
                baseResponse.data as T[]
                : [] as T[]
            : [] as T[];
    }


    private changeURL(endpoint: string): string {
        const route = endpoint.split('/');
        endpoint = 'index.php?';

        for (let i = 0; i < route.length; i++) {
            const element = route[i];

            endpoint += 'p' + (i + 1) + '=' + element;

            if (i < route.length - 1) {
                endpoint += '&';
            }
        }

        return endpoint;
    }

    private injectParams(endpoint: string, data?: ParamsInterface) {
        // Endpoints que NO deben llevar id_cliente ni id_usuario por URL
        const blockList = [
            'user/register',
            'user/login',
            'user/getusers',
            'usuarios/post',
            'usuarios/put'
        ];

        const cleanEndpoint = endpoint.replace('index.php?', '');

        if (blockList.some(b => cleanEndpoint.includes(b))) {
            return endpoint;
        }

        // Normal injection
        for (const x in this.inject.params) {
            if (!Object.prototype.hasOwnProperty.call(this.inject.params, x)) continue;

            const value = this.inject.params[x as keyof typeof this.inject.params]?.(endpoint, data ?? {});

            if (value !== undefined && value !== null) {
                endpoint += '&' + x + '=' + value;
            }
        }

        return endpoint;
    }


    private proxy(
        type: HttpRequestType, 
        endpoint: string, 
        data: ParamsInterface | undefined, 
        callback: { 
            (): Promise<BaseResponseModel<T>>; 
            (): Promise<BaseResponseModel<T>>; 
            (): Promise<BaseResponseModel<T>>; 
            (): Promise<BaseResponseModel<T>>; 
            (): Promise<BaseResponseModel<T>>; 
        }): Promise<BaseResponseModel<T>> {
        if (this.proxyCheck(type, endpoint, data)) {
            return callback();
        } else {
            return new Promise((_resolve, reject) => reject());
        }
    }

    private injectPayload(data: Record<string, string>, endpoint: string) {
        // Lista de endpoints que NO deben recibir id_cliente ni id_usuario
        const blockList = [
            'user/register',
            'user/login',
            'user/getusers',
            'usuarios/post',
            'usuarios/put'
        ];

        // Detectamos el endpoint real
        const cleanEndpoint = endpoint.replace('index.php?', '');

        // Si el endpoint está en bloqueados → no inyectar nada
        if (blockList.some(b => cleanEndpoint.includes(b))) {
            return data;
        }

        // --- Comportamiento normal (para endpoints que sí lo necesitan) ---
        if (data) {
            for (const x in this.inject.payload) {
                if (Object.prototype.hasOwnProperty.call(this.inject.payload, x)) {
                    const value = this.inject.payload[x as keyof typeof this.inject.payload]?.(endpoint, data);

                    if (value !== undefined && value !== null) {
                        data[x] = value;
                    }
                }
            }
        }

        return data;
    }


    /** Request the CSRF cookie so Angular can send X-XSRF-TOKEN automatically. */
    private getCsrfCookie(): Promise<void> {
        return this.http.get<void>(`${this.BASE_URL}sanctum/csrf-cookie`, { withCredentials: true })
        .toPromise()
        .then(() => undefined);
    }

    private _hasCsrf = false;

    private async ensureCsrfOnce(): Promise<void> {
        if (this._hasCsrf) { return; }
        await this.getCsrfCookie();
        this._hasCsrf = true;
    }
}

